// frontend/packages/business-console/src/services/invoiceService.js
//
// Thin wrapper over /api/v2/crm/invoices (work item 2.12). Business
// context is supplied via the x-business-id header (axios interceptor
// in @upryzze/services); callers don't pass businessId.

import { api } from '@upryzze/services';

/**
 * GET /api/v2/crm/invoices
 * @param {object} params
 *   state?      — filter (draft|sent|viewed|partial|paid|overdue|void)
 *   customerId? — filter by customer
 *   q?          — search (matches INV-#### prefix + customer first/last name)
 *   before?     — cursor (ISO date; pagination)
 *   limit?      — page size (default 25, max 100)
 */
export async function listInvoices(params = {}) {
  const { data } = await api.get('/v2/crm/invoices', { params });
  return data || { items: [], hasMore: false, nextCursor: null };
}

export async function getInvoice(id) {
  const { data } = await api.get(`/v2/crm/invoices/${encodeURIComponent(id)}`);
  return data;
}

export async function createInvoiceDraft(payload, { idempotencyKey } = {}) {
  const config = idempotencyKey
    ? { headers: { 'Idempotency-Key': idempotencyKey } }
    : undefined;
  const { data } = await api.post('/v2/crm/invoices', payload, config);
  return data;
}

export async function updateInvoiceDraft(id, payload, { idempotencyKey } = {}) {
  const config = idempotencyKey
    ? { headers: { 'Idempotency-Key': idempotencyKey } }
    : undefined;
  const { data } = await api.patch(
    `/v2/crm/invoices/${encodeURIComponent(id)}`,
    payload,
    config
  );
  return data;
}

export async function sendInvoice(id) {
  const { data } = await api.post(
    `/v2/crm/invoices/${encodeURIComponent(id)}/send`
  );
  return data;
}

export async function voidInvoice(id, reason) {
  const { data } = await api.post(
    `/v2/crm/invoices/${encodeURIComponent(id)}/void`,
    reason ? { reason } : {}
  );
  return data;
}

export async function listInvoicePayments(id) {
  const { data } = await api.get(
    `/v2/crm/invoices/${encodeURIComponent(id)}/payments`
  );
  return data || { items: [] };
}

export async function createInvoicePaymentIntent(id) {
  const { data } = await api.post(
    `/v2/crm/invoices/${encodeURIComponent(id)}/payment-intent`
  );
  return data;
}

export async function resendInvoiceReceipt(id) {
  const { data } = await api.post(
    `/v2/crm/invoices/${encodeURIComponent(id)}/resend-receipt`
  );
  return data;
}

/**
 * Operator-initiated refund on a specific payment (work item 3.12).
 * Issues the Stripe refund; the local refund row appears once the
 * charge.refunded webhook lands (a few seconds).
 * @param {string} invoiceId
 * @param {string} paymentId
 * @param {{ amount?: number, reason?: string }} opts amount in CENTS; omit for full remaining
 */
export async function createPaymentRefund(
  invoiceId,
  paymentId,
  { amount, reason } = {}
) {
  const body = {};
  if (amount != null) body.amount = amount;
  if (reason) body.reason = reason;
  const { data } = await api.post(
    `/v2/crm/invoices/${encodeURIComponent(invoiceId)}/payments/${encodeURIComponent(
      paymentId
    )}/refund`,
    body
  );
  return data;
}

export async function deleteInvoiceDraft(id) {
  const { data } = await api.delete(
    `/v2/crm/invoices/${encodeURIComponent(id)}`
  );
  return data;
}
